import type { ExternalTableSchema } from '@packages/table-builder';
import { IMPORT_EXPORT_SEMANTIC_IDS } from '@packages/table-builder/utils/importExportSemanticIds';

/** Display names from the shared TransactionStatus seed. */
export const RSA_TRANSACTION_STATUS_BADGES = {
  Created: 'default',
  'Ready For Execution': 'info',
  InProgress: 'info',
  Successful: 'success',
  Failed: 'error',
  Cancelled: 'error',
  Reversed: 'warning',
} as const;

export const rsaPurchaseTable: ExternalTableSchema = {
  metadata: {
    title: 'RSA Workflow',
    entity: 'RsaPurchase',
  },

  dataSource: {
    endpoint: '/api/award/api/app/rsa-transaction/r-sAPurchase-list',
    method: 'GET',
    rowIdField: 'id',
    responseMapping: {
      items: 'items',
      totalCount: 'totalCount',
    },
    requestParams: {
      Filter: 'table.searchText',
      MaxResultCount: 'table.pageSize',
      SkipCount: 'table.pageOffset',
      Sorting: 'table.sorting',
    },
    filters: [
      {
        name: 'FromDate',
        placeholder: 'Purchase Date From',
        type: 'dateRange',
        rangeEndField: 'ToDate',
        rangeEndPlaceholder: 'Purchase Date To',
      },
      {
        name: 'SettlementTypeIds',
        placeholder: 'Settlement Type',
        type: 'asyncSelect',
        multiple: true,
        dataSource: {
          url: '/api/award/api/app/rsa-transaction/r-sASettlement-types',
          labelField: 'displayName',
          valueField: 'id',
        },
      },
      {
        name: 'Is83bElected',
        placeholder: '83(b) Election',
        type: 'select',
        options: [
          { label: 'Elected', value: true },
          { label: 'Not Elected', value: false },
        ],
      },
      {
        name: 'GrantNumber',
        placeholder: 'Grant / Award Number',
        type: 'text',
      },
    ],
  },

  columns: [
    {
      field: 'participantName',
      header: 'Participant Details',
      type: 'couple',
      sortable: true,
      width: 220,
      couple: { titleField: 'participantName', subtitleField: 'employeeId' },
    },
    { field: 'grantNumber', header: 'Grant / Award Number', type: 'text', sortable: true, width: 180 },
    { field: 'purchaseDate', header: 'Purchase Date', type: 'date', sortable: true, width: 150 },
    { field: 'shares', header: 'Shares', type: 'number', sortable: true, width: 130, align: 'center' },
    { field: 'purchasePrice', header: 'Purchase Price $', type: 'currency', sortable: true, width: 160, align: 'center', format: { decimals: 4 } },
    { field: 'fmv', header: 'FMV $', type: 'currency', sortable: true, width: 140, align: 'center', format: { decimals: 4 } },
    { field: 'cost', header: 'Cost $', type: 'currency', sortable: true, width: 140, align: 'center', format: { decimals: 4 }, visibility: 'picker' },
    {
      field: 'is83bElected',
      header: '83(b) Election',
      type: 'badge',
      sortable: true,
      width: 160,
      align: 'center',
      conditionalValues: { true: 'ELECTED', false: 'NOT ELECTED' },
      badge: { statusMap: { true: 'success', false: 'default' }, defaultVariant: 'default' },
    },
    { field: 'settlementType', header: 'Settlement Type', type: 'text', sortable: true, width: 160 },
    { field: 'fundingRequired', header: 'Funding Required $', type: 'currency', sortable: true, width: 170, align: 'center', format: { decimals: 4 }, visibility: 'picker' },
    { field: 'fundedAmount', header: 'Funded Amount $', type: 'currency', sortable: true, width: 160, align: 'center', format: { decimals: 4 }, visibility: 'picker' },
    { field: 'totalTaxes', header: 'Total Taxes $', type: 'currency', sortable: true, width: 150, align: 'center', format: { decimals: 4 }, visibility: 'picker' },
    { field: 'repurchaseShares', header: 'Repurchase Shares', type: 'number', sortable: true, width: 160, align: 'center', visibility: 'picker' },
    { field: 'netShares', header: 'Net Shares', type: 'number', sortable: true, width: 130, align: 'center', visibility: 'picker' },
    { field: 'shareDestination', header: 'Share Destination', type: 'text', sortable: true, width: 170, visibility: 'picker' },
    {
      field: 'transactionStatus',
      header: 'Status',
      type: 'badge',
      sortable: true,
      width: 150,
      align: 'center',
      badge: { statusMap: RSA_TRANSACTION_STATUS_BADGES, defaultVariant: 'default' },
    },
  ],

  actions: {
    row: [
      { id: 'VIEW_PURCHASE_RSA', label: 'View Vest Events' },
      { id: 'VIEW_DETAILS_PURCHASE_RSA', label: 'View Details' },
      { id: 'PUBLISH_PURCHASE_RSA', label: 'Publish' },
    ],
    toolbar: {
      secondary: [
        {
          label: 'Download Template',
          icon: 'download',
          type: 'download',
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.downloadTemplate,
          children: [
            { id: 'DOWNLOAD_PURCHASE_TEMPLATE_RSA', label: 'RSA Purchase Template' },
            { id: 'DOWNLOAD_VEST_EVENT_TEMPLATE_RSA', label: 'RSA Vest Event Template' },
          ],
        },
        {
          label: 'Import Bulk XLS',
          icon: 'upload',
          type: 'upload',
          semanticId: IMPORT_EXPORT_SEMANTIC_IDS.importTemplate,
          children: [
            { id: 'IMPORT_PURCHASE_RSA', label: 'Import RSA Purchases' },
            { id: 'IMPORT_VEST_EVENT_RSA', label: 'Import RSA Vest Events' },
          ],
        },
      ],
    },
  },

  features: {
    search: { enabled: true },
    pagination: { mode: 'infiniteScroll', defaultSize: 50 },
    columnPreferences: { persistenceKey: 'RsaPurchase', resizable: true, reorderable: true },
    filterGrid: { columns: 3 },
  },
};
